import React, { Component } from 'react';
import { Location } from 'history';
import moment from 'moment';
import axios from 'axios';
import history from '../../../history';
import { ROOT_URL_V2 } from '../../../config';
import { executeHttpRequest } from '../../../epics/api';
import { withPushFiltersToUrl } from '../../operations/common/decorators';
import { TPageNotification, TUser } from '../../../types';
import PayrollAdminFilters from './PayrollAdminFilters';
import PayrollAdminTable, { TAgencyTimesheetSummary } from './PayrollAdminTable';

type TPayrollAdminProps = {
    location: Location;
    notification: TPageNotification;
    onFilterChange: (id: string, value: any) => void;
    user: TUser;
};

type TPayrollAdminState = {
    content: TAgencyTimesheetSummary[];
    fetching: boolean;
    error?: string;
    autoApprovingAgencyId?: number;
};

class PayrollAdmin extends Component<TPayrollAdminProps, TPayrollAdminState> {
    state: TPayrollAdminState = {
        content: [],
        fetching: false,
        error: undefined,
        autoApprovingAgencyId: undefined,
    };

    componentDidMount() {
        const { search } = history.location;
        const currentUrlParams = new URLSearchParams(search);
        if (search) {
            this.load(currentUrlParams);
        } else {
            const paramString = new URLSearchParams(
                this.getDefaultQueryParams() as Record<string, string>,
            ).toString();
            history.push({
                pathname: '/payments/payroll-admin/',
                search: paramString,
            });
        }
    }

    componentDidUpdate(prevProps: TPayrollAdminProps) {
        if (prevProps.location?.search !== this.props.location?.search) {
            this.load(new URLSearchParams(history.location.search));
        }
    }

    getDefaultQueryParams = () => ({
        bookings_after: moment()
            .startOf('isoWeek')
            .startOf('day')
            .subtract(1, 'week')
            .toISOString(),
        bookings_before: moment()
            .endOf('isoWeek')
            .endOf('day')
            .subtract(1, 'week')
            .toISOString(),
    });

    canAutoApproveTimesheets = () =>
        Boolean(
            this.props.user?.permissions?.includes(
                'auth.can_auto_approve_timesheets',
            ),
        );

    load = async(searchParams: URLSearchParams) => {
        const startDate = searchParams.get('bookings_after');
        const endDate = searchParams.get('bookings_before');
        if (!startDate || !endDate) {
            this.props.notification.error('Error', 'Please select a date range.');
            return;
        }

        const formattedStartDate = moment(startDate);
        const formattedEndDate = moment(endDate);
        if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) {
            return;
        }

        this.setState({ fetching: true, error: undefined });
        try {
            const response = await executeHttpRequest(
                `timesheets/agency-timesheet-summary/?start_date=${encodeURIComponent(
                    formattedStartDate.format('YYYY-MM-DD'),
                )}&end_date=${encodeURIComponent(
                    formattedEndDate.format('YYYY-MM-DD'),
                )}`,
            );
            this.setState({
                content: response.data?.content || [],
                fetching: false,
                error: undefined,
            });
        } catch (error) {
            this.setState({
                fetching: false,
                error: 'Failed to load agency summary.',
            });
            this.props.notification.error(
                'Error',
                'Failed to load agency summary.',
            );
        }
    };

    handleDownload = async(agencyId: number, onlyNew = false) => {
        const searchParams = new URLSearchParams(history.location.search);
        const startDate = searchParams.get('bookings_after');
        const endDate = searchParams.get('bookings_before');
        const formattedStartDate = moment(startDate);
        const formattedEndDate = moment(endDate);
        if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) {
            this.props.notification.error(
                'Error',
                'Please select a valid date range.',
            );
            return;
        }

        const onlyNewParam = onlyNew ? '&only_new=True' : '';
        const downloadUrl = `${ROOT_URL_V2}timesheets/?agency_id=${encodeURIComponent(
            String(agencyId),
        )}&start_date=${encodeURIComponent(
            formattedStartDate.format('YYYY-MM-DD'),
        )}&end_date=${encodeURIComponent(
            formattedEndDate.format('YYYY-MM-DD'),
        )}&format=csv${onlyNewParam}`;

        try {
            const response = await axios({
                method: 'get',
                url: downloadUrl,
                responseType: 'blob',
            });

            const contentType = response.headers?.['content-type'] || '';
            if (contentType.includes('application/json')) {
                const text = await response.data.text();
                let message = 'No timesheets available for export.';
                try {
                    const parsed = JSON.parse(text) as {
                        meta?: {
                            processing?: number;
                            processing_email?: string;
                        };
                        content?: { message?: string };
                    };
                    const meta = parsed?.meta;
                    const processingEmail = meta?.processing_email;
                    const hasAsyncExport =
                        Boolean(processingEmail) &&
                        meta?.processing !== undefined &&
                        meta?.processing !== null;
                    if (hasAsyncExport) {
                        message = `The CSV is being generated and will be sent to ${processingEmail}.`;
                    } else {
                        message = parsed?.content?.message || message;
                    }
                } catch (e) {
                    // fall through with default message
                }
                this.props.notification.info('Info', message);
                return;
            }

            const blob = new Blob([response.data], { type: 'text/csv' });
            const objectUrl = window.URL.createObjectURL(blob);

            const disposition = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename =
                match?.[1] ||
                `timesheets_${agencyId}_${formattedStartDate.format(
                    'YYYY-MM-DD',
                )}_${formattedEndDate.format('YYYY-MM-DD')}${
                    onlyNew ? '_new' : ''
                }.csv`;

            const link = document.createElement('a');
            link.href = objectUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);

            await this.load(new URLSearchParams(history.location.search));
        } catch (error) {
            this.props.notification.error(
                'Error',
                'Failed to download timesheets CSV.',
            );
        }
    };

    handleAutoApprove = async(agencyId: number) => {
        if (!this.canAutoApproveTimesheets()) {
            this.props.notification.error(
                'Error',
                'You do not have permission to auto-approve timesheets.',
            );
            return;
        }

        const searchParams = new URLSearchParams(history.location.search);
        const startDate = searchParams.get('bookings_after');
        const endDate = searchParams.get('bookings_before');
        const formattedStartDate = moment(startDate);
        const formattedEndDate = moment(endDate);
        if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) {
            this.props.notification.error(
                'Error',
                'Please select a valid date range.',
            );
            return;
        }

        this.setState({ autoApprovingAgencyId: agencyId });
        try {
            const response = await executeHttpRequest('timesheets/auto-approve/', 'post', {
                agency_id: agencyId,
                start_date: formattedStartDate.format('YYYY-MM-DD'),
                end_date: formattedEndDate.format('YYYY-MM-DD'),
            });

            const updatedCount = response?.data?.content?.updated_count ?? 0;
            if (updatedCount === 0) {
                this.props.notification.info(
                    'Info',
                    'No timesheets were available to auto-approve.',
                );
            } else {
                this.props.notification.success(
                    'Success',
                    `${updatedCount} timesheets auto-approved.`,
                );
            }
            await this.load(new URLSearchParams(history.location.search));
        } catch (error) {
            this.props.notification.error(
                'Error',
                'Failed to auto-approve timesheets.',
            );
        } finally {
            this.setState({ autoApprovingAgencyId: undefined });
        }
    };

    render() {
        const canAutoApprove = this.canAutoApproveTimesheets();

        return (
            <div className="table-container">
                <PayrollAdminFilters
                    onFilterChange={this.props.onFilterChange}
                    reloadData={() =>
                        this.load(new URLSearchParams(history.location.search))
                    }
                />
                <PayrollAdminTable
                    loading={this.state.fetching}
                    content={this.state.content}
                    errorMsg={this.state.error}
                    onDownload={this.handleDownload}
                    onAutoApprove={this.handleAutoApprove}
                    canAutoApprove={canAutoApprove}
                    autoApprovingAgencyId={this.state.autoApprovingAgencyId}
                />
            </div>
        );
    }
}

export default withPushFiltersToUrl(PayrollAdmin);
